import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modale Établissement — mode consultation + sélection.
 *
 * Comportement :
 *  - Filtre `Type` (---Tous---, Détention, Réadaptation, Psy)
 *  - Dropdown `Liste` montrant les établissements filtrés
 *  - Champs READ-ONLY (gérés par l'admin — pas d'édition côté utilisateur)
 *  - `Enregistrer` : associe l'établissement au dossier (persiste id_etablis)
 *                    + ferme la modale. Actif dès qu'une ligne est sélectionnée.
 *  - `Fermer` : ferme sans modifier la sélection courante.
 *
 * Layout compact (peu de padding) pour tenir dans l'écran sans scroll.
 */
const EtablissementModal = ({ isOpen, onClose, onSelect, currentEtablis }) => {
  const [etablissements, setEtablissements] = useState([]);
  const [types, setTypes] = useState([]);
  const [typeFiltre, setTypeFiltre] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reference/etablissements`);
      setEtablissements(data || []);
      setTypes([...new Set((data || []).map(e => e.type).filter(Boolean))].sort());
      if (currentEtablis?.id_etablis) {
        const found = (data || []).find(e => e.id_etablis === currentEtablis.id_etablis);
        if (found) {
          setSelectedId(String(found.id_etablis));
          setForm({ ...found });
        }
      }
    } catch (e) {
      console.error('[EtablissementModal] Erreur chargement établissements:', e?.message);
    }
  }, [currentEtablis]);

  useEffect(() => {
    if (isOpen) {
      reload();
    } else {
      setSelectedId('');
      setForm(null);
      setTypeFiltre('');
    }
  }, [isOpen, reload]);

  const filtered = typeFiltre
    ? etablissements.filter(e => e.type === typeFiltre)
    : etablissements;

  const handleSelectChange = (id) => {
    setSelectedId(id);
    const found = etablissements.find(e => String(e.id_etablis) === id);
    setForm(found ? { ...found } : null);
  };

  const handleEnregistrer = () => {
    if (form?.id_etablis) {
      onSelect && onSelect(form);
      onClose();
    }
  };

  const handleFermer = () => {
    onClose();
  };

  if (!isOpen) return null;

  // Style compact : labels en colonne fixe, inputs alignés, gap réduit.
  const row = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 };
  const lab = { width: 95, marginBottom: 0, fontWeight: 'bold', fontSize: 11, color: '#003366' };
  // IMPORTANT : pas de `height` fixé sur les `<select>` natifs (le texte sélectionné
  // est rendu PAR-DESSUS le padding interne — fixer une hauteur trop petite tronque
  // verticalement le texte sélectionné). On laisse le browser dimensionner.
  const selectStyle = { flex: 1, fontSize: 12, padding: '2px 4px', lineHeight: '1.4', minHeight: 26 };
  const inp = (k) => ({
    className: 'win-input',
    value: form?.[k] ?? '',
    readOnly: true,
    tabIndex: -1,
    style: { background: '#f0f0f0', cursor: 'default', flex: 1, fontSize: 12, padding: '3px 6px', height: 22 },
    'data-testid': `etab-input-${k.toLowerCase()}`,
  });

  return (
    <div className="popup-overlay" onClick={handleFermer}>
      <div
        className="etab-modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 580, maxHeight: '92vh', overflowY: 'auto' }}
        data-testid="etablissement-modal"
      >
        <div className="popup-header" style={{ background: '#0033A0', padding: '6px 12px' }}>
          <span className="popup-title">Établissement</span>
          <button className="win-modal-close" onClick={handleFermer} data-testid="etab-btn-x">×</button>
        </div>

        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column' }}>
          {/* TYPE filter */}
          <div style={row}>
            <label style={lab}>Type</label>
            <select
              className="win-select"
              value={typeFiltre}
              onChange={e => {
                setTypeFiltre(e.target.value);
                setSelectedId('');
                setForm(null);
              }}
              style={selectStyle}
              data-testid="etab-select-type"
            >
              <option value="">---Tous---</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* LISTE dropdown */}
          <div style={row}>
            <label style={lab}>Liste</label>
            <select
              className="win-select"
              value={selectedId}
              onChange={e => handleSelectChange(e.target.value)}
              style={selectStyle}
              data-testid="etab-select-liste"
            >
              <option value=""></option>
              {filtered.map(e => (
                <option key={e.id_etablis} value={String(e.id_etablis)}>
                  {e.id_etablis} - {e.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Champs READ-ONLY — affichés systématiquement (vide si rien
              sélectionné) pour éviter le re-flow et garder le modal compact. */}
          <div style={{ borderTop: '1px solid #d0d0d0', marginTop: 6, paddingTop: 6 }}>
            <div style={row}>
              <label style={lab}>Nom</label>
              <input {...inp('nom')} />
            </div>
            <div style={row}>
              <label style={lab}>Adresse</label>
              <input {...inp('adresse')} />
            </div>
            <div style={row}>
              <label style={lab}>Ville</label>
              <input {...inp('ville')} />
            </div>
            <div style={row}>
              <label style={lab}>Province</label>
              <input {...inp('province')} style={{ ...inp('province').style, width: 70, flex: 'none' }} />
              <label style={{ ...lab, width: 90, textAlign: 'right' }}>Code postal</label>
              <input {...inp('code_postal')} style={{ ...inp('code_postal').style, width: 110, flex: 'none' }} />
            </div>
            <div style={row}>
              <label style={lab}>Téléphone</label>
              <input {...inp('telephone')} style={{ ...inp('telephone').style, width: 140, flex: 'none' }} />
              <label style={{ ...lab, width: 40, textAlign: 'right' }}>Ext.</label>
              <input {...inp('extention')} style={{ ...inp('extention').style, width: 70, flex: 'none' }} />
            </div>
            <div style={row}>
              <label style={lab}>Cellulaire</label>
              <input {...inp('Cellulaire')} style={{ ...inp('Cellulaire').style, width: 140, flex: 'none' }} />
            </div>
            <div style={row}>
              <label style={lab}>Courriel</label>
              <input {...inp('courriel')} />
            </div>
          </div>
        </div>

        <div
          className="popup-footer"
          style={{ borderTop: '1px solid #e0e0e0', gap: 10, padding: '8px 14px' }}
        >
          <button
            className="popup-btn"
            onClick={handleEnregistrer}
            disabled={!form?.id_etablis}
            style={{
              background: !form?.id_etablis ? '#888' : '#0033A0',
              cursor: !form?.id_etablis ? 'not-allowed' : 'pointer',
            }}
            data-testid="etab-btn-enregistrer"
            title={form?.id_etablis ? 'Associer cet établissement au dossier' : 'Sélectionnez d\'abord un établissement'}
          >
            Enregistrer
          </button>
          <button
            className="popup-btn"
            onClick={handleFermer}
            style={{ background: '#666' }}
            data-testid="etab-btn-fermer"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default EtablissementModal;
